import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Clock, Calendar } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { format } from "date-fns";
import { ActivityType, LeadActivity, activityTypeLabels } from "../types";

interface LeadActivitiesProps {
  leadId: string;
  activities: LeadActivity[];
  onUpdate: () => void;
}

export const LeadActivities = ({ leadId, activities = [], onUpdate }: LeadActivitiesProps) => {
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    activity_type: "call" as ActivityType,
    title: "",
    description: "",
    performed_at: new Date().toISOString().slice(0, 16), // Format: YYYY-MM-DDThh:mm
  });
  
  const resetForm = () => {
    setFormData({
      activity_type: "call",
      title: "",
      description: "",
      performed_at: new Date().toISOString().slice(0, 16),
    });
  };
  
  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Get current authenticated user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("Authentication required");
      }
      
      const { error } = await supabase
        .from("lead_activities")
        .insert({
          lead_id: leadId,
          activity_type: formData.activity_type,
          title: formData.title,
          description: formData.description || null,
          performed_at: formData.performed_at,
          created_by: user.id
        });
        
      if (error) throw error;
      
      toast.success("Activity added successfully");
      setShowForm(false);
      resetForm();
      onUpdate();
      
    } catch (error) {
      console.error("Error saving activity:", error);
      toast.error("Failed to save activity");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const getActivityIcon = (activityType: ActivityType) => {
    switch (activityType) {
      case "call":
        return <Clock className="h-8 w-8 text-blue-500" />;
      case "email":
        return <Calendar className="h-8 w-8 text-green-500" />;
      case "meeting":
        return <Calendar className="h-8 w-8 text-purple-500" />;
      case "note":
        return <Calendar className="h-8 w-8 text-gray-500" />;
      case "linkedin":
        return <Calendar className="h-8 w-8 text-blue-700" />;
      case "demo":
        return <Calendar className="h-8 w-8 text-orange-500" />;
      case "proposal":
        return <Calendar className="h-8 w-8 text-amber-500" />;
      default:
        return <Calendar className="h-8 w-8 text-gray-500" />;
    }
  };
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Activities</CardTitle>
        <Button onClick={() => setShowForm(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Activity
        </Button>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No activities yet. Add your first activity for this lead.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {activities.map((activity) => (
              <Card key={activity.id}>
                <div className="p-4 flex items-center gap-4">
                  <div>
                    {getActivityIcon(activity.activity_type)}
                  </div>
                  <div>
                    <h4 className="font-medium">{activity.title}</h4>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {activity.description || "No description"}
                    </p>
                    <div className="mt-2 text-xs text-muted-foreground">
                      {format(new Date(activity.performed_at), 'MMM d, yyyy h:mm a')}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </CardContent>
      
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Activity</DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="activity_type">Activity Type</Label>
              <Select
                value={formData.activity_type}
                onValueChange={(value) => setFormData({ ...formData, activity_type: value as ActivityType })}
              >
                <SelectTrigger id="activity_type">
                  <SelectValue placeholder="Select activity type" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(activityTypeLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Initial Call, Sent Proposal"
                required
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of the activity"
                rows={3}
              />
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="performed_at">Performed At</Label>
              <Input
                type="datetime-local"
                id="performed_at"
                value={formData.performed_at}
                onChange={(e) => setFormData({ ...formData, performed_at: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Add Activity"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
